import { useMemo, useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store/rootReducers';
import { setLocation } from '../store/slices/LocationSlice';

/**
 * Shared hook for Post and Shorts: use location when postsReelsByLocation is true.
 * When postsReelsByLocation is on but Redux has no location, fetches current position and sets it.
 * isLocationLoading is true from first render when we need location but don't have it (so feed waits before calling API).
 */
export function useFeedLocation() {
  const dispatch = useDispatch();
  const [locationFetchFailed, setLocationFetchFailed] = useState(false);
  const postsReelsByLocation = useSelector(
    (state: RootState) => state.accountPrivacy.postsReelsByLocation
  );
  const reduxLocation = useSelector(
    (state: RootState) => state.location?.location
  );
  const fetchAttempted = useRef(false);

  // True when we need location, don't have it yet, and haven't failed → feed should wait
  const isLocationLoading =
    Boolean(postsReelsByLocation) &&
    !reduxLocation &&
    !locationFetchFailed;

  // When feed needs location but Redux has none, try to get current position and set it
  useEffect(() => {
    if (!postsReelsByLocation || reduxLocation || !navigator.geolocation) {
      if (postsReelsByLocation && !reduxLocation && !navigator.geolocation) {
        setLocationFetchFailed(true);
      }
      return;
    }
    if (fetchAttempted.current) return;
    fetchAttempted.current = true;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        dispatch(
          setLocation({
            lat: latitude,
            lng: longitude,
            name: `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`,
          })
        );
      },
      () => {
        fetchAttempted.current = false;
        setLocationFetchFailed(true);
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 }
    );
  }, [postsReelsByLocation, reduxLocation, dispatch]);

  const locationForPosts = useMemo(
    () =>
      postsReelsByLocation && reduxLocation
        ? { latitude: reduxLocation.lat, longitude: reduxLocation.lng }
        : undefined,
    [postsReelsByLocation, reduxLocation]
  );
  return { postsReelsByLocation, locationForPosts, isLocationLoading };
}
